import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Eye, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      toast.error('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
      case 'expired':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400',
      completed: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      initiated: 'bg-blue-500/20 text-blue-400',
      failed: 'bg-red-500/20 text-red-400',
      expired: 'bg-stone-500/20 text-stone-400',
      cancelled: 'bg-stone-500/20 text-stone-400'
    };
    return styles[status] || 'bg-stone-500/20 text-stone-400';
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.product_title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || order.payment_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalRevenue = orders
    .filter(o => o.payment_status === 'paid')
    .reduce((sum, o) => sum + o.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Orders</h1>
          <p className="text-stone-400 text-sm">{orders.length} total orders</p>
        </div>
        <div className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-xs text-stone-400">Total Revenue</p>
              <p className="text-lg font-semibold text-white">${totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-terracota"
        >
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-700">
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Customer</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Product</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Amount</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Status</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Date</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-stone-400">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="border-b border-stone-700/50 hover:bg-stone-700/30">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-white text-sm">{order.customer_name}</p>
                        <p className="text-stone-500 text-xs">{order.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white text-sm">{order.product_title}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white font-medium">${order.amount.toFixed(2)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${getStatusBadge(order.payment_status)}`}>
                        {getStatusIcon(order.payment_status)}
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-stone-400 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 text-stone-400 hover:text-white transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-stone-800 rounded-xl w-full max-w-lg border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <h2 className="text-lg font-semibold text-white">Order Details</h2>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="text-stone-400 hover:text-white"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-400 text-sm">Order ID</p>
                  <p className="text-white text-sm font-mono">{selectedOrder.id.slice(0, 8)}...</p>
                </div>
                <div>
                  <p className="text-stone-400 text-sm">Status</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${getStatusBadge(selectedOrder.payment_status)}`}>
                    {selectedOrder.payment_status}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-stone-400 text-sm">Customer</p>
                <p className="text-white">{selectedOrder.customer_name}</p>
                <p className="text-stone-400 text-sm">{selectedOrder.customer_email}</p>
              </div>
              <div>
                <p className="text-stone-400 text-sm">Product</p>
                <p className="text-white">{selectedOrder.product_title}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-400 text-sm">Amount</p>
                  <p className="text-white font-semibold">${selectedOrder.amount.toFixed(2)} {selectedOrder.currency.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-sm">Date</p>
                  <p className="text-white">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
              </div>
              {selectedOrder.stripe_session_id && (
                <div>
                  <p className="text-stone-400 text-sm">Stripe Session</p>
                  <p className="text-white text-xs font-mono break-all">{selectedOrder.stripe_session_id}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
